import api from "./api";
import { Job, JobStats } from "@/types";

/**
 * Service layer for Job Posting API endpoints.
 * Base path: /api/v1/job-postings
 *
 * Note: GET /job-postings returns data as a flat array (not { docs: [...] }).
 */
export const jobService = {
  /**
   * Fetch all job postings.
   * API returns array directly after envelope unwrap: data = [...]
   * @returns Array of Job items
   */
  getJobPostings: async (params?: Record<string, any>): Promise<Job[]> => {
    const data = await api.get<unknown, Job[] | Record<string, unknown>>(
      "/job-postings",
      { params }
    );

    const jobs = Array.isArray(data)
      ? data
      : Array.isArray((data as Record<string, unknown>).docs)
        ? ((data as Record<string, unknown>).docs as Job[])
        : [];

    return jobs.map((item) => ({
      ...item,
      id: (item as unknown as Record<string, string>)._id || item.id,
    }));
  },

  /**
   * Fetch a specific job posting by its ID.
   * @param id - The job posting ID
   */
  getJobById: async (id: string): Promise<Job> => {
    const raw: Record<string, unknown> = await api.get(`/job-postings/${id}`);
    const job = (raw.doc || raw) as Record<string, unknown>;
    return {
      ...(job as unknown as Job),
      id: (job._id as string) || (job.id as string),
    };
  },

  /**
   * Fetch a specific job posting by its slug.
   * @param slug - The job posting slug
   */
  getJobBySlug: async (slug: string): Promise<Job> => {
    const raw: Record<string, unknown> = await api.get(
      `/job-postings/${slug}`,
    );
    const job = (raw.doc || raw) as Record<string, unknown>;
    return {
      ...(job as unknown as Job),
      id: (job._id as string) || (job.id as string),
    };
  },

  /**
   * Create a new job posting.
   * @param data - The job posting data
   */
  createJob: async (data: Partial<Job>): Promise<Job> => {
    const raw: Record<string, unknown> = await api.post("/job-postings", data);
    const job = (raw.doc || raw) as Record<string, unknown>;
    return {
      ...(job as unknown as Job),
      id: (job._id as string) || (job.id as string),
    };
  },

  /**
   * Update an existing job posting.
   * @param id - The job posting ID
   * @param data - The update data
   */
  updateJob: async (id: string, data: Partial<Job>): Promise<Job> => {
    const raw: Record<string, unknown> = await api.patch(
      `/job-postings/${id}`,
      data,
    );
    const job = (raw.doc || raw) as Record<string, unknown>;
    return {
      ...(job as unknown as Job),
      id: (job._id as string) || (job.id as string),
    };
  },

  /**
   * Delete a job posting.
   * @param id - The job posting ID
   */
  deleteJob: async (id: string): Promise<void> => {
    await api.delete(`/job-postings/${id}`);
  },

  /**
   * Fetch recruitment statistics.
   * @returns JobStats object { openJobs, totalApplications, interviewsScheduled }
   */
  getJobStats: async (): Promise<JobStats> => {
    const raw: any = await api.get("/job-postings/stats");
    return raw as JobStats;
  },
};
